import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/cn'
import { FULL_FRAME, MIN_CROP, type CropRect } from '../model'
import styles from '../styles/crop.module.scss'

export interface CropOverlayProps {
  /** Current rectangle, in fractions of the source frame. */
  crop: CropRect
  /** Source frame aspect (width / height). A ratio lock is expressed in
   * *pixels*, so turning it into a fraction ratio needs this. */
  frameAspect: number
  /** Locked output ratio (width / height in pixels), or ``null`` for free. */
  ratio: number | null
  onChange: (crop: CropRect) => void
}

type Handle = 'nw' | 'ne' | 'sw' | 'se' | 'move'

const HANDLES: Exclude<Handle, 'move'>[] = ['nw', 'ne', 'sw', 'se']

/** Keyboard nudge, as a fraction of the frame. Fine enough to land on a
 * composition, coarse enough to cross the frame in a few seconds held. */
const NUDGE = 0.005
const NUDGE_COARSE = 0.05

/**
 * The framing handles, drawn on the **output frame** rather than over the
 * whole source.
 *
 * That is the whole interaction: the preview always shows the framing you
 * have chosen — opening this tool never un-crops the picture — so the
 * rectangle *is* the visible box. Pulling a corner outwards grows the crop
 * and reveals more footage as you drag; pulling it in tightens the frame;
 * dragging the middle pans the window across the source.
 *
 * Fractions throughout, like every other geometry in this editor: the same
 * rectangle has to mean the same thing over a 720p proxy in a box of
 * whatever size the layout gives it and over the 4K master ffmpeg crops.
 * Since the box shows ``crop.width`` of the source, a pixel of drag is
 * ``crop.width / boxWidth`` of the source — the conversion below.
 *
 * The ratio lock resizes from the **opposite corner**, which is what makes
 * a corner drag feel like it's pulling the frame rather than sliding it —
 * the standard behaviour in every crop tool.
 */
export function CropOverlay({ crop, frameAspect, ratio, onChange }: CropOverlayProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const [drag, setDrag] = useState<{
    handle: Handle
    originX: number
    originY: number
    origin: CropRect
    /** Box size at mousedown. Frozen for the whole drag: the box rescales
     * as the crop changes, and re-reading it every move would make the
     * rectangle accelerate away from the cursor. */
    boxWidth: number
    boxHeight: number
  } | null>(null)

  // A locked ratio is given in pixels; the rectangle is in fractions of a
  // frame that isn't square, so the fraction-space ratio is scaled by it.
  const fractionRatio = ratio === null ? null : ratio / frameAspect

  useEffect(() => {
    if (!drag) return
    if (drag.boxWidth <= 0 || drag.boxHeight <= 0) return

    const onMove = (e: MouseEvent) => {
      // Pixels of the box are fractions of the *visible* crop, so scaling
      // by the crop's own size converts them to fractions of the source.
      const dx = ((e.clientX - drag.originX) / drag.boxWidth) * drag.origin.width
      const dy = ((e.clientY - drag.originY) / drag.boxHeight) * drag.origin.height
      onChange(applyDrag(drag.origin, drag.handle, dx, dy, fractionRatio))
    }
    const onUp = () => {
      setDrag(null)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [drag, fractionRatio, onChange])

  const begin = (e: React.MouseEvent, handle: Handle) => {
    if (e.button !== 0) return
    e.stopPropagation()
    e.preventDefault()
    const rect = hostRef.current?.getBoundingClientRect()
    if (!rect) return
    setDrag({
      handle,
      originX: e.clientX,
      originY: e.clientY,
      origin: crop,
      boxWidth: rect.width,
      boxHeight: rect.height,
    })
  }

  const nudge = (e: React.KeyboardEvent) => {
    const step = (e.shiftKey ? NUDGE_COARSE : NUDGE) * (e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1)
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault()
      onChange(applyDrag(crop, 'move', step, 0, fractionRatio))
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault()
      onChange(applyDrag(crop, 'move', 0, step, fractionRatio))
    }
  }

  return (
    <div
      ref={hostRef}
      className={cn(styles.host, drag && styles.hostDragging)}
      onMouseDown={(e) => {
        begin(e, 'move')
      }}
      onKeyDown={nudge}
      role="button"
      tabIndex={0}
      aria-label="Enquadramento — arraste para deslocar, puxe os cantos para redimensionar"
      title="Arraste para deslocar · puxe os cantos para abrir ou fechar o enquadramento"
    >
      {/* Thirds, the framing guide every crop tool draws. */}
      <div className={styles.thirds} aria-hidden>
        <span />
        <span />
        <span />
        <span />
      </div>
      {HANDLES.map((h) => (
        <span
          key={h}
          className={cn(styles.handle, styles[h])}
          onMouseDown={(e) => {
            begin(e, h)
          }}
          role="presentation"
        />
      ))}
    </div>
  )
}

/**
 * Where a drag lands, clamped inside the frame.
 *
 * Kept out of the component (and exported) so the rule is testable: the
 * clamping is where crop bugs live, and "the rectangle escaped the frame"
 * fails the export rather than the UI, with ffmpeg refusing the filter.
 */
export function applyDrag(
  origin: CropRect,
  handle: Handle,
  dx: number,
  dy: number,
  ratio: number | null,
): CropRect {
  if (handle === 'move') {
    return {
      ...origin,
      x: clamp(origin.x + dx, 0, 1 - origin.width),
      y: clamp(origin.y + dy, 0, 1 - origin.height),
    }
  }

  const west = handle === 'nw' || handle === 'sw'
  const north = handle === 'nw' || handle === 'ne'
  // The opposite corner is the anchor — that's what makes the drag pull the
  // frame instead of sliding it.
  const anchorX = west ? origin.x + origin.width : origin.x
  const anchorY = north ? origin.y + origin.height : origin.y

  let width = clamp(west ? origin.width - dx : origin.width + dx, MIN_CROP, 1)
  let height = clamp(north ? origin.height - dy : origin.height + dy, MIN_CROP, 1)

  if (ratio !== null) {
    // Follow whichever axis moved more, so the drag tracks the cursor on
    // the side the user is actually pulling.
    if (Math.abs(dx) >= Math.abs(dy)) height = width / ratio
    else width = height * ratio
    // A ratio can push the other side out of the frame; shrink to fit
    // rather than break the lock.
    const maxWidth = west ? anchorX : 1 - anchorX
    const maxHeight = north ? anchorY : 1 - anchorY
    const scale = Math.min(1, maxWidth / width, maxHeight / height)
    width *= scale
    height *= scale
    width = Math.max(MIN_CROP, width)
    height = Math.max(MIN_CROP, height)
  } else {
    width = Math.min(width, west ? anchorX : 1 - anchorX)
    height = Math.min(height, north ? anchorY : 1 - anchorY)
  }

  const x = west ? anchorX - width : anchorX
  const y = north ? anchorY - height : anchorY
  return {
    x: clamp(x, 0, 1 - width),
    y: clamp(y, 0, 1 - height),
    width,
    height,
  }
}

/**
 * Largest rectangle of ``ratio`` (pixels) that fits the frame, centred.
 * What picking a preset gives you before any dragging — the same "fit and
 * centre" every editor starts a locked crop from.
 */
export function fitRatio(frameAspect: number, ratio: number | null): CropRect {
  if (ratio === null) return { ...FULL_FRAME }
  const target = ratio / frameAspect
  let width = 1
  let height = width / target
  if (height > 1) {
    height = 1
    width = height * target
  }
  return { x: (1 - width) / 2, y: (1 - height) / 2, width, height }
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v))
}
